import type { schedule as SCH } from '@cwos/api-client';
import {
  ATTRIBUTION_CONSEQUENCE,
  ATTRIBUTION_LABEL,
  ATTRIBUTION_OPTIONS,
} from './cancellationLogic.js';

/**
 * WHOSE SIDE DID THIS CANCELLATION COME FROM — asked once, with no default.
 *
 * ---------------------------------------------------------------------------
 * WHY THE EMPTY OPTION IS NOT A SIXTH ATTRIBUTION
 * ---------------------------------------------------------------------------
 * `''` means NOBODY HAS CHOSEN YET, and the confirm button its callers draw
 * stays disabled while it holds. That is the whole reason this is a control and
 * not a hidden constant: a defaulted picker would write the same attribution on
 * every cancellation in the organization, and a column full of a value nobody
 * chose is worse than one full of nulls because it LOOKS answered.
 *
 * `'unknown'` sits at the end of the list, offered rather than hidden. A
 * scheduler who genuinely does not know needs somewhere honest to go; taking the
 * option away would push them into picking one that is wrong, and "not known" is
 * a different fact from "nobody recorded anything".
 *
 * ---------------------------------------------------------------------------
 * THE CONSEQUENCE IS SHOWN BEFORE THE CLICK, NOT AFTER
 * ---------------------------------------------------------------------------
 * `worker` is the only member that puts a QUESTION TO A PERSON: the server
 * raises the single ask, with a deadline, and escalates it to that person's
 * manager if it goes unanswered. A scheduler choosing from five words with no
 * indication that one of them starts a clock on a colleague is being asked to
 * make a consequential choice blind. So the selected member's consequence is
 * rendered under the control, from the same record the options come from.
 */
export function AttributionPicker({
  value,
  onChange,
  id,
  /** Render the consequence sentence. Off in the cramped list row. */
  explain = false,
}: {
  value: SCH.Attribution | '';
  onChange: (next: SCH.Attribution | '') => void;
  id: string;
  explain?: boolean;
}) {
  return (
    <span className="rn-col" style={{ gap: 'var(--space-4)' }}>
      <label className="rn-muted" htmlFor={id}>
        Attributable to
      </label>
      <select
        id={id}
        value={value}
        onClick={(e) => e.stopPropagation()}
        onChange={(e) => onChange(e.target.value as SCH.Attribution | '')}
      >
        <option value="">Choose who this is attributable to…</option>
        {ATTRIBUTION_OPTIONS.map((option) => (
          <option key={option} value={option}>
            {ATTRIBUTION_LABEL[option]}
          </option>
        ))}
      </select>
      {explain && value !== '' ? (
        <span className="rn-muted">{ATTRIBUTION_CONSEQUENCE[value]}</span>
      ) : null}
    </span>
  );
}
